import React, { useState, useEffect, useRef } from 'react';
import Header from './common/Header'
import Footer from './common/Footer'
import SEO from '../components/common/SEO';
import './HotelsListing.css';
import './SearchSuggestions.css';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginPopup from './popup/LoginPopup';
import ReviewModal from './popup/ReviewModal';
import axios from 'axios';
import { toast } from "react-toastify";
import { getValidSession } from "./helpers/sessionUtils";
import { buildVendorProfileUrl } from '../utils/vendorProfileUrl';
import ListingVerifiedBadge from '../components/common/ListingVerifiedBadge';
import VendorProfileButton from '../components/common/VendorProfileButton';
import ListingFilterBar from '../components/common/ListingFilterBar';
import { formatPrice } from '../utils/priceUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const vendorProfileUrl = (item) => buildVendorProfileUrl(item?.Vendor);

// ─── STAR RATING COMPONENT ────────────────────────────────────────────────────
const StarRating = ({ avg = 0, count = 0, onClick }) => {
    const fullStars = Math.floor(avg);
    const hasHalf = avg - fullStars >= 0.5;

    return (
        <div
            className="d-flex align-items-center justify-content-center gap-2"
            style={{ cursor: onClick ? 'pointer' : 'default' }}
            onClick={onClick}
        >
            <div className="d-flex" style={{ gap: '2px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <i
                        key={star}
                        className={`bi ${star <= fullStars
                            ? 'bi-star-fill'
                            : star === fullStars + 1 && hasHalf
                                ? 'bi-star-half'
                                : 'bi-star'
                            } text-warning`}
                        style={{ fontSize: '16px' }}
                    />
                ))}
            </div>
            <span className="fw-bold text-dark" style={{ fontSize: '14px' }}>
                {Number(avg || 0).toFixed(1)}/5
            </span>
            <span className="text-muted" style={{ fontSize: '12px' }}>
                ({count} reviews)
            </span>
        </div>
    );
};

// ─── AMENITY ICON HELPER ──────────────────────────────────────────────────────
const ADMIN_AMENITY_ICONS = {
    'wifi': 'ri-wifi-line',
    'parking': 'ri-car-line',
    'swimming pool': 'ri-water-flash-line',
    'restaurant': 'ri-restaurant-2-line',
    'pet friendly': 'ri-baidu-line',
    'gym/fitness center': 'ri-run-line',
    'air conditioning': 'ri-windy-line',
    'room service': 'ri-customer-service-2-line',
    'telephone': 'ri-phone-line',
    'geyser / hot water': 'ri-shower-line',
    'spa': 'ri-plant-line',
    'bar/lounge': 'ri-goblet-line',
};

const getAmenityIcon = (amenity) => {
    const a = String(amenity || '').trim().toLowerCase();
    if (ADMIN_AMENITY_ICONS[a]) return ADMIN_AMENITY_ICONS[a];
    if (a.includes('wifi') || a.includes('internet')) return 'ri-wifi-line';
    if (a.includes('parking')) return 'ri-car-line';
    if (a.includes('pool') || a.includes('swimming')) return 'ri-water-flash-line';
    if (a.includes('restaurant') || a.includes('breakfast') || a.includes('meal') || a.includes('dining')) return 'ri-restaurant-2-line';
    if (a.includes('pet')) return 'ri-baidu-line';
    if (a.includes('gym') || a.includes('fitness')) return 'ri-run-line';
    if (a.includes('ac') || a.includes('air conditioning') || a.includes('cooling')) return 'ri-windy-line';
    if (a.includes('room service')) return 'ri-customer-service-2-line';
    if (a.includes('phone') || a.includes('telephone') || a.includes('contact')) return 'ri-phone-line';
    if (a.includes('geyser') || a.includes('hot water') || a.includes('shower')) return 'ri-shower-line';
    if (a.includes('spa') || a.includes('massage')) return 'ri-plant-line';
    if (a.includes('bar') || a.includes('lounge') || a.includes('drink')) return 'ri-goblet-line';
    return 'ri-checkbox-circle-line';
};

// ─── HOTEL CARD COMPONENT ─────────────────────────────────────────────────────
const HotelCard = ({ pkg, onAction, onCardClick, onReviewClick, isSubmitting, pendingAction, activeId }) => {
    const isActive = activeId === pkg.id && isSubmitting;

    const hotelImages = Array.isArray(pkg.Images) ? pkg.Images : [];
    const preferredImage =
        hotelImages.find((image) => image.image_size_name === 'large') ||
        hotelImages.reduce((best, image) => {
            const sizePriority = { large: 4, card: 3, profile: 2, thumbnail: 1 };
            return (sizePriority[image.image_size_name] || 0) > (sizePriority[best?.image_size_name] || 0)
                ? image
                : best;
        }, null) ||
        hotelImages[0];
    const displayImage = preferredImage?.image_url ||
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=95';

    let amenities = [];
    if (typeof pkg.amenities === 'string') {
        try { amenities = JSON.parse(pkg.amenities); } catch (e) { /* noop */ }
    } else if (Array.isArray(pkg.amenities)) {
        amenities = pkg.amenities;
    }
    const visibleAmenities = amenities.slice(0, 5);
    const hiddenAmenitiesCount = Math.max(amenities.length - visibleAmenities.length, 0);

    return (
        <div
            className="card mb-2 shadow-sm rounded-2 bg-white overflow-hidden hotel-result-card"
            style={{ border: '1px solid #e0e0e0', cursor: 'default' }}
        >
            <div className="row g-0 align-items-stretch">

                {/* ── IMAGE ─────────────────────────────────────── */}
                <div className="col-12 col-md-4 col-lg-3 p-0 d-flex align-items-stretch justify-content-stretch bg-light-subtle">
                    <div className="overflow-hidden w-100 position-relative" style={{ minHeight: 220 }}>
                        <img
                            src={displayImage}
                            alt={pkg.hotel_name}
                            className="w-100 h-100"
                            style={{
                                objectFit: 'cover',
                                display: 'block',
                                cursor: 'pointer',
                                filter: 'none',
                                opacity: 1
                            }}
                            onClick={() => onCardClick(pkg)}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                    'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=95';
                            }}
                        />
                        <ListingVerifiedBadge item={pkg} />
                        {/* VERIFIED BADGE - TOP LEFT */}
                        {/* Price badge top-right */}
                        <div className="position-absolute badge bg-primary price hotel-listing-price-badge ">
                            {pkg.offer_price && pkg.hotel_price && Number(pkg.hotel_price) > Number(pkg.offer_price) ? (
                                <>
                                    <del className="text-white-50 me-1" style={{ fontSize: 11, textDecoration: 'line-through' }}>
                                        ₹{formatPrice(Math.round(pkg.hotel_price))}
                                    </del>
                                    <span>₹{formatPrice(Math.round(pkg.offer_price))}</span>
                                </>
                            ) : (
                                <span>₹{formatPrice(Math.round(pkg.offer_price || pkg.hotel_price || 0))}</span>
                            )}
                            <span style={{ fontWeight: 400, fontSize: 11 }}>/night</span>
                        </div>
                    </div>
                </div>

                {/* ── INFO ──────────────────────────────────────── */}
                <div
                    className="col-12 col-md-8 col-lg-6 p-3 text-start"
                    style={{ cursor: 'pointer' }}
                    onClick={() => onCardClick(pkg)}
                >
                    {/* Title */}
                    <h4
                        className=""
                        style={{ color: '#333', fontSize: 18 }}
                    >
                        {pkg.hotel_name}
                    </h4>

                    {/* Location badge */}
                    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                        <span className="badge bg-light text-dark" style={{ fontSize: 12 }}>
                            <i className="bi bi-geo-alt-fill me-1" style={{ color: '#dc3545' }}></i>
                            {pkg.city}, {pkg.state}
                        </span>
                        {vendorProfileUrl(pkg) && (
                            <VendorProfileButton to={vendorProfileUrl(pkg)} />
                        )}
                    </div>

                    {/* Description */}
                    <p className="mb-3 hotel-card-description" style={{ fontSize: 14, lineHeight: '1.5' }}>
                        {pkg.description?.length > 120 ? `${pkg.description.substring(0, 120)}...` : pkg.description}
                    </p>

                    {/* Amenities */}
                    <div className="d-flex flex-wrap gap-3 hotel-amenities-list">
                        {amenities.length > 0 ? (
                            <>
                            {visibleAmenities.map((amenity, idx) => (
                                <span
                                    key={idx}
                                    className="badge hotel-amenity-chip"
                                >
                                    <i className={`${getAmenityIcon(amenity)} me-2`}></i>
                                    {amenity}
                                </span>
                            ))}
                            {hiddenAmenitiesCount > 0 && (
                                <span
                                    className="badge hotel-amenity-chip hotel-amenity-more"
                                >
                                    <i className="ri-add-circle-line me-2"></i>
                                    +{hiddenAmenitiesCount} more amenities
                                </span>
                            )}
                            </>
                        ) : (
                            <span style={{ color: '#4b5563', fontSize: 13 }}>No amenities listed</span>
                        )}
                    </div>
                </div>

                {/* ── BUTTONS ───────────────────────────────────── */}
                <div className="col-12 col-lg-3 p-3 p-lg-4 d-flex flex-column gap-2 border-start hotel-card-actions">
                    {/* Star Rating */}
                    <div className="d-none d-lg-block mb-lg-3 pb-lg-3 border-bottom">
                        <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                    </div>

                    <div className="d-none d-md-block d-lg-none mb-2 pb-2 border-bottom text-center">
                        <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                    </div>

                    {/* Mobile: review summary left, call/WhatsApp icons right */}
                    <div className="hotel-mobile-action-row d-md-none">
                        <div className="hotel-mobile-rating">
                            <StarRating avg={pkg.avgRating || 0} count={pkg.reviewCount || 0} onClick={() => onReviewClick(pkg)} />
                        </div>
                        <div className="hotel-mobile-icons">
                            <button
                                className="hotel-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'call', pkg)}
                                disabled={isSubmitting}
                                title="Call Now"
                            >
                                {isActive && pendingAction === 'call' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/call.png" alt="Call" />
                                )}
                            </button>
                            <button
                                className="hotel-mobile-icon-btn"
                                onClick={(e) => onAction(e, 'whatsapp', pkg)}
                                disabled={isSubmitting}
                                title="WhatsApp Now"
                            >
                                {isActive && pendingAction === 'whatsapp' ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    <img src="/assets/img/mobile-icon/whatsapp.png" alt="WhatsApp" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Tablet: compact icon-only buttons in a row */}
                    <div className="d-none d-md-flex d-lg-none gap-2 justify-content-center">
                        <button
                            className="btn btn-outline-primary fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'call', pkg)}
                            disabled={isSubmitting}
                            title="Call Now"
                        >
                            {isActive && pendingAction === 'call' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-telephone-fill"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-success fw-bold rounded-3 d-flex align-items-center justify-content-center"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'whatsapp', pkg)}
                            disabled={isSubmitting}
                            title="WhatsApp Now"
                        >
                            {isActive && pendingAction === 'whatsapp' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-whatsapp"></i>
                            )}
                        </button>
                        <button
                            className="btn btn-outline-dark fw-bold rounded-3 d-flex align-items-center justify-content-center listing-book-btn"
                            style={{ width: '52px', height: '44px', flexShrink: 0, cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'book', pkg)}
                            disabled={isSubmitting}
                            title="Book Now"
                        >
                            {isActive && pendingAction === 'book' ? (
                                <span className="spinner-border spinner-border-sm"></span>
                            ) : (
                                <i className="bi bi-calendar-check-fill"></i>
                            )}
                        </button>
                    </div>

                    {/* Desktop: stacked full-width buttons */}
                    <div className="d-none d-lg-flex flex-column gap-2">
                        {/* Call Now */}
                        <button
                            className="btn btn-outline-primary fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'call', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'call' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-telephone-fill me-2"></i>
                            )}
                            {isActive && pendingAction === 'call' ? 'Connecting...' : 'Call Now'}
                        </button>

                        {/* WhatsApp */}
                        <button
                            className="btn btn-outline-success fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'whatsapp', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'whatsapp' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="bi bi-whatsapp me-2"></i>
                            )}
                            {isActive && pendingAction === 'whatsapp' ? 'Connecting...' : 'WhatsApp Now'}
                        </button>

                        {/* Book Now */}
                        <button
                            className="btn btn-outline-dark fw-bold py-2 rounded-3 d-flex align-items-center justify-content-center w-100 listing-book-btn"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => onAction(e, 'book', pkg)}
                            disabled={isSubmitting}
                        >
                            {isActive && pendingAction === 'book' ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : null}
                            {isActive && pendingAction === 'book' ? 'Processing...' : 'Book Now'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── PAGINATION COMPONENT ─────────────────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else if (currentPage <= 3) {
            for (let i = 1; i <= maxVisible; i++) pages.push(i);
            pages.push('...'); pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
            pages.push(1); pages.push('...');
            for (let i = totalPages - maxVisible + 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1); pages.push('...');
            for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
            pages.push('...'); pages.push(totalPages);
        }
        return pages;
    };

    return (
        <nav aria-label="Page navigation" className="listing-pagination-nav">
            <ul className="listing-pagination-list">
                <li className={`listing-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                        className="listing-page-link"
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                    >
                        <i className="bi bi-chevron-left"></i> Previous
                    </button>
                </li>

                {getPageNumbers().map((page, idx) => (
                    <li key={idx} className={`listing-page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                        <button
                            className={`listing-page-link ${page === currentPage ? 'active' : ''}`}
                            onClick={() => typeof page === 'number' && onPageChange(page)}
                            disabled={page === '...'}
                        >
                            {page}
                        </button>
                    </li>
                ))}

                <li className={`listing-page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                        className="listing-page-link"
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                    >
                        Next <i className="bi bi-chevron-right"></i>
                    </button>
                </li>
            </ul>
        </nav>
    );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
function HotelsListing() {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const cityFromUrl = queryParams.get('city') || '';
    const hotelFromUrl = queryParams.get('hotel') || '';
    const qFromUrl = queryParams.get('q') || '';
    const initialQuery = cityFromUrl || hotelFromUrl || qFromUrl || '';

    const hotelsPerPage = 20;

    const [priceSort, setPriceSort] = useState('');
    const [isTopRated, setIsTopRated] = useState(false);
    const [isVerifiedOnly, setIsVerifiedOnly] = useState(false);

    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [suggestions, setSuggestions] = useState([]);
    const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const skipNextFetchRef = useRef(false);
    const searchContainerRef = useRef(null);

    const [appliedLocation, setAppliedLocation] = useState(cityFromUrl || qFromUrl || '');
    const [appliedHotel, setAppliedHotel] = useState(hotelFromUrl);
    const [packages, setPackages] = useState([]);
    const [visiblePackages, setVisiblePackages] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [defaultPackages, setDefaultPackages] = useState([]);
    const [visibleDefaultPackages, setVisibleDefaultPackages] = useState([]);
    const [defaultPage, setDefaultPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    const [showThanksModal, setShowThanksModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeEntity, setActiveEntity] = useState(null);
    const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);

    // ── REVIEW STATE ───────────────────────────────────────────────────────────
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [reviewIntent, setReviewIntent] = useState(false); // true = login was triggered by review click
    const loginSuccessRef = useRef(false);

    const [user, setUser] = useState(() => {
        const session = getValidSession();
        return session?.user?.user || session?.user || null;
    });

    // Click outside handler for search suggestions dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const session = getValidSession();
        setUser(session?.user?.user || session?.user || null);
    }, [isLoginPopupOpen]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const city = params.get('city') || '';
        const hotel = params.get('hotel') || '';
        const q = params.get('q') || '';
        const activeSearch = city || hotel || q || '';
        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        setSearchQuery(activeSearch);
        setAppliedLocation(city || q || '');
        setAppliedHotel(hotel);
    }, [location.search]);

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        if (searchQuery.trim().length < 2) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }
        const debounceTimer = setTimeout(() => {
            const fetchSuggestions = async () => {
                setIsSuggestionsLoading(true);
                try {
                    const response = await axios.get(`${API_BASE_URL}/hotels/search?q=${encodeURIComponent(searchQuery.trim())}`);
                    if (response.data && Array.isArray(response.data)) {
                        setSuggestions(response.data);
                        if (response.data.length > 0) {
                            setShowSuggestions(true);
                        }
                    } else {
                        setSuggestions([]);
                        setShowSuggestions(false);
                    }
                } catch (error) {
                    console.error("Failed to fetch suggestions:", error);
                    setSuggestions([]);
                    setShowSuggestions(false);
                } finally {
                    setIsSuggestionsLoading(false);
                }
            };
            fetchSuggestions();
        }, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchQuery]);

    // Fetch hotels
    useEffect(() => {
        const fetchHotels = async () => {
            setIsLoading(true);
            try {
                let url = `${API_BASE_URL}/hotels/hotel-list`;
                const params = [];
                if (appliedLocation) params.push(`city=${encodeURIComponent(appliedLocation)}`);
                if (appliedHotel) params.push(`hotel=${encodeURIComponent(appliedHotel)}`);
                if (params.length > 0) url += `?${params.join('&')}`;

                const response = await axios.get(url);
                const data = (response?.data?.data && Array.isArray(response.data.data)) ? response.data.data : [];
                setPackages(data);
                setCurrentPage(1);

                // If search returns 0 results and a search query is active, fetch all default hotels
                if (data.length === 0 && (appliedLocation || appliedHotel)) {
                    try {
                        const defResponse = await axios.get(`${API_BASE_URL}/hotels/hotel-list`);
                        const defData = (defResponse?.data?.data && Array.isArray(defResponse.data.data)) ? defResponse.data.data : [];
                        setDefaultPackages(defData);
                        setVisibleDefaultPackages(defData.slice(0, hotelsPerPage));
                        setDefaultPage(1);
                    } catch (defErr) {
                        console.error('Failed to fetch default hotels:', defErr);
                        setDefaultPackages([]);
                        setVisibleDefaultPackages([]);
                    }
                } else {
                    setDefaultPackages([]);
                    setVisibleDefaultPackages([]);
                }
            } catch (error) {
                console.error('Failed to fetch hotels:', error);
                setPackages([]);
                setDefaultPackages([]);
                setVisibleDefaultPackages([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchHotels();
    }, [appliedLocation, appliedHotel]);

    // Filter & Sort Hotels
    const filteredHotels = React.useMemo(() => {
        let list = [...packages];

        // 1. Filter by Verified Vendors only (Aadhaar verified)
        if (isVerifiedOnly) {
            list = list.filter(item => {
                const vendor = item?.Vendor || item?.vendor;
                return vendor?.is_publicly_verified === true || Number(vendor?.is_publicly_verified) === 1;
            });
        }

        const getPrice = (item) => Number(item.offer_price) || Number(item.hotel_price) || 0;
        const getRating = (item) => Number(item.avgRating || item.avg_rating || 0);
        const getReviewCount = (item) => Number(item.reviewCount || item.review_count || 0);

        // 2. Sort by Top Rated or Price
        if (isTopRated) {
            list.sort((a, b) => {
                const rDiff = getRating(b) - getRating(a);
                if (rDiff !== 0) return rDiff;
                const cDiff = getReviewCount(b) - getReviewCount(a);
                if (cDiff !== 0) return cDiff;
                if (priceSort === 'low_to_high') return getPrice(a) - getPrice(b);
                if (priceSort === 'high_to_low') return getPrice(b) - getPrice(a);
                return 0;
            });
        } else if (priceSort === 'low_to_high') {
            list.sort((a, b) => getPrice(a) - getPrice(b));
        } else if (priceSort === 'high_to_low') {
            list.sort((a, b) => getPrice(b) - getPrice(a));
        }

        return list;
    }, [packages, isVerifiedOnly, isTopRated, priceSort]);

    // Reset page to 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [isVerifiedOnly, isTopRated, priceSort]);

    // Update visible hotels when filtered list or current page changes
    useEffect(() => {
        const startIndex = (currentPage - 1) * hotelsPerPage;
        const endIndex = startIndex + hotelsPerPage;
        setVisiblePackages(filteredHotels.slice(startIndex, endIndex));
    }, [filteredHotels, currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDefaultPageChange = (pageNumber) => {
        const startIndex = (pageNumber - 1) * hotelsPerPage;
        const endIndex = startIndex + hotelsPerPage;
        setVisibleDefaultPackages(defaultPackages.slice(startIndex, endIndex));
        setDefaultPage(pageNumber);
        window.scrollTo({ top: 400, behavior: 'smooth' });
    };

    const slugify = (str) =>
        str
            ? String(str)
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^\w-]+/g, '')
                .replace(/--+/g, '-')
                .replace(/^-+/, '')
                .replace(/-+$/, '')
            : '';

    // Expected /hotel-details/:slug format in HotelDetails.js:
    // <hotelname>-<city>-<price>-<id>
    const generateHotelSlug = (hotel) => {
        const name = slugify(hotel?.hotel_name || hotel?.name);
        const city = slugify(hotel?.city || hotel?.location_city);
        const price = Math.round(hotel?.offer_price || hotel?.hotel_price || hotel?.price || 0);
        const id = hotel?.id;

        return [name, city, price ? price : null, id]
            .filter(Boolean)
            .join('-')
            .replace(/--+/g, '-');
    };

    // Redirect using the slug-based detail route
    const handleCardClick = (pkg) => {
        const slug = generateHotelSlug(pkg);
        window.open(`/hotel-details/${slug}`, '_blank', 'noopener,noreferrer');
    };



    // ── LEAD FLOW ──────────────────────────────────────────────────────────────
    const handleAction = (e, type, pkg) => {
        e.preventDefault();
        e.stopPropagation();
        if (isSubmitting) return;

        setReviewIntent(false); // not a review action
        setActiveEntity(pkg);
        setPendingAction(type);

        if (!user) {
            setIsLoginPopupOpen(true);
            return;
        }
        processLead(type, user, pkg);
    };

    const handleLoginSuccess = (loggedInUser) => {
        loginSuccessRef.current = true;
        const session = getValidSession();
        const resolvedUser = session?.user?.user || session?.user || loggedInUser;
        setUser(resolvedUser);
        setIsLoginPopupOpen(false);

        if (reviewIntent && selectedHotel) {
            setReviewIntent(false);
            setShowReviewModal(true);
        } else if (pendingAction && activeEntity) {
            processLead(pendingAction, resolvedUser, activeEntity);
        }
    };

    const handleLoginPopupClose = () => {
        setIsLoginPopupOpen(false);
        setIsSubmitting(false);
        setTimeout(() => {
            if (!loginSuccessRef.current && pendingAction && activeEntity) {
                setPendingAction(null);
                setActiveEntity(null);
            }
            loginSuccessRef.current = false;
        }, 0);
    };

    // ── REVIEW HANDLERS ────────────────────────────────────────────────────────
    const handleReviewClick = (hotel) => {
        setSelectedHotel(hotel);
        if (!user) {
            setReviewIntent(true);
            setIsLoginPopupOpen(true);
            return;
        }
        setShowReviewModal(true);
    };

    const processLead = async (actionType, userData, pkg) => {
        setIsSubmitting(true);
        const currentUser = userData || user;

        const payload = {
            name: currentUser?.name || 'Guest',
            email: currentUser?.email || '',
            mobile: (currentUser?.phone || currentUser?.mobile || '').replace(/^(\+91|91)/, ''),
            type: 'hotel',
            type_id: pkg.id,
            message: `User clicked ${actionType} for ${pkg.hotel_name}`
        };

        try {
            const res = await axios.post(`${API_BASE_URL}/enquiries/create`, payload);
            if (res.data.success) {
                setShowThanksModal(true);
            } else {
                toast.error(res.data?.message || 'Could not process request. Please try again.');
                setPendingAction(null);
            }
        } catch (err) {
            toast.error('Error processing request. Please try again.');
            console.error(err);
            setIsSubmitting(false);
            setPendingAction(null);
            setActiveEntity(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleThankYouClose = () => {
        setShowThanksModal(false);
        setPendingAction(null);
        setActiveEntity(null);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        const trimmed = searchQuery.trim();
        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        if (trimmed) {
            navigate(`/hotel-list?city=${encodeURIComponent(trimmed)}`);
            setAppliedLocation(trimmed);
            setAppliedHotel('');
        } else {
            navigate('/hotel-list');
            setAppliedLocation('');
            setAppliedHotel('');
        }
    };

    const handleSuggestionClick = (suggestion) => {
        skipNextFetchRef.current = true;
        setShowSuggestions(false);
        setSuggestions([]);
        setSearchQuery(suggestion.name);
        if (suggestion.type === 'city') {
            navigate(`/hotel-list?city=${encodeURIComponent(suggestion.name)}`);
            setAppliedLocation(suggestion.name);
            setAppliedHotel('');
        } else if (suggestion.type === 'hotel') {
            const slug = generateHotelSlug(suggestion);
            window.open(`/hotel-details/${slug}`, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div>
            <SEO
                title={appliedLocation ? `Hotels in ${appliedLocation} | Mangoloops` : 'Luxury Hotels & Stays | Mangoloops'}
                description="Find and book the best luxury hotels and stays at Mangoloops. Explore top-rated accommodation with premium amenities."
            />
            <Header />

            {/* ── SEARCH BAR ──────────────────────────────────── */}
            <div className="banner-content tour-search-sec text-center mb-5 mb-lg-8">
                <div className="container-fluid">
                    <div className="row justify-content-center py-xl-4">
                        <div className="col-lg-8">
                            <div className="card-body">
                                <form className="form-container search-input-form" onSubmit={handleSearchSubmit}>
                                    <div className="search-container page-search-autocomplete" ref={searchContainerRef}>
                                        <span><i className="fa fa-search search-icon"></i></span>
                                        <input
                                            type="text"
                                            placeholder="Enter City, Hotel, or Address"
                                            className="search-input"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                skipNextFetchRef.current = false;
                                                setShowSuggestions(true);
                                                setSearchQuery(val);
                                                if (val === '') {
                                                    setSuggestions([]);
                                                    setShowSuggestions(false);
                                                    setAppliedLocation('');
                                                    setAppliedHotel('');
                                                    navigate('/hotel-list');
                                                }
                                            }}
                                            onFocus={() => {
                                                if (searchQuery.trim().length >= 2 && suggestions.length > 0 && !skipNextFetchRef.current) {
                                                    setShowSuggestions(true);
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                    setShowSuggestions(false);
                                                }
                                            }}
                                            autoComplete="off"
                                        />

                                        <button type="submit" className="search-button">Search</button>
                                        {showSuggestions && isSuggestionsLoading && (
                                            <div className="suggestions-dropdown"><div className="suggestion-item">Loading...</div></div>
                                        )}
                                        {showSuggestions && !isSuggestionsLoading && suggestions.length > 0 && (
                                            <div className="suggestions-dropdown">
                                                {suggestions.map((item, index) => (
                                                    <div
                                                        key={`${item.type}-${item.id || index}`}
                                                        className="suggestion-item"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            handleSuggestionClick(item);
                                                        }}
                                                    >
                                                        <strong>{item.name}</strong>
                                                        <small>— {item.type === 'hotel' ? 'Hotel' : 'City'}{item.city ? ` · ${item.city}` : ''}</small>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── RESULTS ─────────────────────────────────────── */}
            <section className="mt-5 mb-7">
                <div className="container">
                    {/* Result count header */}
                    <div className="row mb-3">
                        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h3 className="tour-title-of-trip holidays-left mb-0 heading-div">
                                {appliedLocation ? `Hotels in ${appliedLocation}` : appliedHotel ? `Hotels for ${appliedHotel}` : 'All Hotels'}
                            </h3>
                            <div className="d-flex align-items-center flex-wrap gap-3">
                                <ListingFilterBar
                                    priceSort={priceSort}
                                    onPriceSortChange={setPriceSort}
                                    isTopRated={isTopRated}
                                    onTopRatedToggle={() => setIsTopRated(prev => !prev)}
                                    isVerifiedOnly={isVerifiedOnly}
                                    onVerifiedToggle={() => setIsVerifiedOnly(prev => !prev)}
                                />
                                {!isLoading && (
                                    <div className="text-muted" style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                                        Showing {visiblePackages.length} results of {filteredHotels.length}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row mt-2">
                        {/* Loading spinner */}
                        {isLoading && (
                            <div className="col-12 text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {!isLoading && visiblePackages.length === 0 && (
                            <div className="col-12">
                                <div className="alert alert-info text-center">
                                    No hotels found for the selected filters.
                                </div>
                            </div>
                        )}

                        {/* Cards when search result has data */}
                        {!isLoading && visiblePackages.map((pkg) => (
                            <div className="col-12 mb-2" key={pkg.id}>
                                <HotelCard
                                    pkg={pkg}
                                    onAction={handleAction}
                                    onCardClick={handleCardClick}
                                    onReviewClick={handleReviewClick}
                                    isSubmitting={isSubmitting}
                                    pendingAction={pendingAction}
                                    activeId={activeEntity?.id}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Pagination for main search results */}
                    {!isLoading && filteredHotels.length > hotelsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(filteredHotels.length / hotelsPerPage)}
                            onPageChange={handlePageChange}
                        />
                    )}

                    {/* Default All Hotels list when search produces 0 results */}
                    {!isLoading && visiblePackages.length === 0 && (appliedLocation || appliedHotel) && defaultPackages.length > 0 && (
                        <div className="mt-5">
                            <div className="row mb-3">
                                <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
                                    <h3 className="tour-title-of-trip holidays-left mb-0 heading-div">
                                        All Hotels
                                    </h3>
                                    <div className="text-muted" style={{ fontSize: 14 }}>
                                        Showing {visibleDefaultPackages.length} of {defaultPackages.length} results
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-2">
                                {visibleDefaultPackages.map((pkg) => (
                                    <div className="col-12 mb-2" key={pkg.id}>
                                        <HotelCard
                                            pkg={pkg}
                                            onAction={handleAction}
                                            onCardClick={handleCardClick}
                                            onReviewClick={handleReviewClick}
                                            isSubmitting={isSubmitting}
                                            pendingAction={pendingAction}
                                            activeId={activeEntity?.id}
                                        />
                                    </div>
                                ))}
                            </div>

                            {defaultPackages.length > hotelsPerPage && (
                                <Pagination
                                    currentPage={defaultPage}
                                    totalPages={Math.ceil(defaultPackages.length / hotelsPerPage)}
                                    onPageChange={handleDefaultPageChange}
                                />
                            )}
                        </div>
                    )}
                </div>
            </section>

            <Footer />

            {/* ── LOGIN POPUP ─────────────────────────────────── */}
            {isLoginPopupOpen && (
                <div
                    className="custom-modal-overlay"
                    onClick={() => {
                        setIsLoginPopupOpen(false);
                        setIsSubmitting(false);
                        setPendingAction(null);
                        setActiveEntity(null);
                    }}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <LoginPopup
                            visible={isLoginPopupOpen}
                            onClose={handleLoginPopupClose}
                            onLoginSuccess={handleLoginSuccess}
                            serviceType="Hotel"
                        />
                    </div>
                </div>
            )}

            {/* ── THANK YOU MODAL ─────────────────────────────── */}
            {(isSubmitting || showThanksModal) && (
                <div className="custom-modal-overlay" style={{ zIndex: 9999999 }}>
                    <div
                        className="thanks-modal card shadow-lg border-0 rounded-4 text-center p-5"
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: 400, background: 'white' }}
                    >
                        {isSubmitting && !showThanksModal ? (
                            <>
                                <div className="mb-4">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', borderWidth: '0.3em' }}>
                                        <span className="visually-hidden">Processing...</span>
                                    </div>
                                </div>
                                <h4 className="fw-bold mb-2">Processing your request...</h4>
                                <p className="text-muted mb-0">Please wait while we save your lead and notify the vendor.</p>
                            </>
                        ) : (
                            <>
                                <div className="mb-4">
                                    <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h2 className="fw-bold mb-3">Thank You!</h2>
                                <p className="text-muted mb-4">
                                    Your request has been sent to the team. We will get back to you shortly.
                                </p>
                                <button
                                    className="btn btn-primary rounded-pill px-4 py-2 fw-bold"
                                    onClick={handleThankYouClose}
                                >
                                    OK
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ── REVIEW MODAL ────────────────────────────────── */}
            {showReviewModal && selectedHotel && (
                <ReviewModal
                    isOpen={showReviewModal}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedHotel(null);
                    }}
                    entity={selectedHotel}
                    entityType="hotel"
                    user={user}
                    apiEndpoint="/hotels"
                />
            )}
        </div>
    );
}

export default HotelsListing;
